import React from 'react';
import {
    Box,
    IconButton,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import EditNoteIcon from '@mui/icons-material/EditNote';
import UndoIcon from '@mui/icons-material/Undo';
import CheckIcon from '@mui/icons-material/Check';
import ReactMarkdown from 'react-markdown';

export default function BodyEditor({
    editableBodyField,
    effectiveArticleBody,
    editingField,
    editingValue,
    alwaysEditable = false,
    canEditFields,
    hasSavedEdit,
    beginEdit,
    cancelEdit,
    applyEdit,
    revertSavedField,
    editStartLabel,
    editApplyLabel,
    editDiscardLabel,
    editRevertSavedLabel,
    onEditingInput,
}) {
    const isEditing = alwaysEditable ? canEditFields : editingField === editableBodyField;

    return (
        <Box className="editable-block-body">
            {isEditing ? (
                <Typography
                    variant="body1"
                    sx={{ whiteSpace: 'pre-wrap', outline: 'none' }}
                    contentEditable
                    suppressContentEditableWarning
                    onInput={onEditingInput}
                >
                    {alwaysEditable ? effectiveArticleBody : editingValue}
                </Typography>
            ) : (
                <ReactMarkdown>{effectiveArticleBody}</ReactMarkdown>
            )}
            {canEditFields && hasSavedEdit(editableBodyField) && (
                <Box
                    component="span"
                    sx={{
                        display: 'inline-flex',
                        verticalAlign: 'middle',
                        ml: 0.5,
                    }}
                >
                    <EditNoteIcon fontSize="small" color="action" />
                </Box>
            )}
            <Stack
                className="edit-controls-body"
                direction="row"
                spacing={0.5}
                sx={{
                    display: canEditFields ? 'inline-flex' : 'none',
                    mt: 0.0,
                    opacity: alwaysEditable ? 0 : (isEditing ? 1 : 0),
                    transition: 'opacity 0.15s ease',
                    '.editable-block-body:hover &': {
                        opacity: alwaysEditable
                            ? (hasSavedEdit(editableBodyField) ? 1 : 0)
                            : (canEditFields ? 1 : 0),
                    },
                }}
            >
                {alwaysEditable ? (
                    <>
                        {hasSavedEdit(editableBodyField) && (
                            <Tooltip title={editRevertSavedLabel}>
                                <IconButton
                                    size="small"
                                    color="error"
                                    disabled={!canEditFields}
                                    onClick={() => revertSavedField(editableBodyField)}
                                >
                                    <UndoIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </>
                ) : isEditing ? (
                    <>
                        <Tooltip title={editDiscardLabel}>
                            <IconButton size="small" color="error" onClick={cancelEdit} disabled={!canEditFields}>
                                <UndoIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={editApplyLabel}>
                            <IconButton size="small" color="success" onClick={applyEdit} disabled={!canEditFields}>
                                <CheckIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </>
                ) : (
                    <>
                        {hasSavedEdit(editableBodyField) && (
                            <Tooltip title={editRevertSavedLabel}>
                                <IconButton
                                    size="small"
                                    color="error"
                                    disabled={!canEditFields}
                                    onClick={() => revertSavedField(editableBodyField)}
                                >
                                    <UndoIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                        <Tooltip title={editStartLabel}>
                            <IconButton
                                size="small"
                                color="primary"
                                disabled={!canEditFields}
                                onClick={() => beginEdit(editableBodyField)}
                            >
                                <EditIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </>
                )}
            </Stack>
        </Box>
    );
}
